import { FileTypeEnum } from '$/utils/upload';
import { ImageHdType, VideoSRMode } from '@/store/useAIEnhanceStore';
import type {
  AIEraserScene,
  BackgroundReplaceParams,
  MagicSRParams,
  VideoHdParams,
} from './types';

export const AI_TOOL_ALGORITHM_URI = {
  ERASER_TEXT_REMOVER: '/v1/text_remover_img_sd_async',
  ERASER_ANTI_GLARE: '/v1/aigc_anti_glare_flow',
  PEOPLE: '/v1/pet_ins_seg_async',
  GLASSES: '/v1/rmeyeglasses',
  WATERMARK_IMAGE: '/v1/eraser_watermark_v2_async',
  WATERMARK_VIDEO: '/v1/videowmremoval_async',
  VIDEO_HD_AI_4K: '/v1/aigcvsrv2_async',
  VIDEO_HD_ALL_IN_ONE: '/v1/hdvideoallinone_async',
  VIDEO_HD_LOW_LIGHT: '/v1/lowlightvideo_async',
  VIDEO_HD_DE_AI: '/v1/hdgenvsr_async',
  VIDEO_HD_GAME: '/v1/hdvideogame_async',
  VIDEO_HD_ANIME: '/v1/hdvideoanimesd_async',
  VIDEO_HD_PORTRAIT: '/v1/hdvideov5_async',
  VIDEO_HD_GOODS: '/v1/hdvideogoods_async',
  CUTOUT: '/v1/sod_2c_async',
  MAGICS_R_HD: '/v1/image_restoration_async',
  MAGICS_R_SUPER: '/v2/image_restoration_async',
  MAGICS_R_AI_SUPER: '/v1/dlbeautymagicsrv2_async',
  MAGICS_R_PORTRAIT_PRO: '/v1/imagefacesr_async',
  MAGICS_R_OLD_PHOTO: 'aigc_rephoto_flow',
  MAGICS_R_TEXT: '/v1/textimagesr_v2_async',
  MAGICS_R_GOODS: '/v1/dlbeautygoodsimagesr_async',
  BACKGROUND_REPLACE_REF: '/v1/aigoodsflux_ref_async',
  BACKGROUND_REPLACE_GENERAL: '/v1/outsourcing_gateway_submit_async',
} as const;

/**
 * 根据 AI 消除场景返回基础消除工具名，手动框选/涂抹仍使用旧文字消除接口。
 */
export function getEraserToolNameByScene(scenes?: AIEraserScene | number) {
  if (scenes === 3) return AI_TOOL_ALGORITHM_URI.PEOPLE;
  if (scenes === 4) return AI_TOOL_ALGORITHM_URI.GLASSES;
  if (scenes === 2) return AI_TOOL_ALGORITHM_URI.ERASER_ANTI_GLARE;
  return AI_TOOL_ALGORITHM_URI.ERASER_TEXT_REMOVER;
}

/**
 * 根据 AI 自动消除场景返回工具名，自动文字消除改走图片去水印 v2 接口。
 */
export function getEraserAutoToolNameByScene(scenes?: AIEraserScene | number) {
  if (scenes === 1) return AI_TOOL_ALGORITHM_URI.WATERMARK_IMAGE;
  return getEraserToolNameByScene(scenes);
}

export function getWatermarkToolName(mediaType?: FileTypeEnum | 'image' | 'video') {
  return mediaType === FileTypeEnum.Video || mediaType === 'video'
    ? AI_TOOL_ALGORITHM_URI.WATERMARK_VIDEO
    : AI_TOOL_ALGORITHM_URI.WATERMARK_IMAGE;
}

/** 根据高清参数选择计费接口名。 */
export function getMagicSRToolName(params: MagicSRParams | VideoHdParams) {
  if ('init_image' in params) return getVideoHdToolName(params);
  if (!params.hd_type) return AI_TOOL_ALGORITHM_URI.VIDEO_HD_AI_4K;
  if (
    params.hd_type === ImageHdType.Super
    || params.hd_type === ImageHdType.Portrait
  ) {
    return AI_TOOL_ALGORITHM_URI.MAGICS_R_SUPER;
  }
  if (params.hd_type === ImageHdType.AISuper) return AI_TOOL_ALGORITHM_URI.MAGICS_R_AI_SUPER;
  if (params.hd_type === ImageHdType.PortraitPro) return AI_TOOL_ALGORITHM_URI.MAGICS_R_PORTRAIT_PRO;
  if (params.hd_type === ImageHdType.OldPicture) return AI_TOOL_ALGORITHM_URI.MAGICS_R_OLD_PHOTO;
  if (params.hd_type === ImageHdType.Text) return AI_TOOL_ALGORITHM_URI.MAGICS_R_TEXT;
  if (params.hd_type === ImageHdType.Goods) return AI_TOOL_ALGORITHM_URI.MAGICS_R_GOODS;
  return AI_TOOL_ALGORITHM_URI.MAGICS_R_HD;
}

/**
 * 根据 video_hd 模式选择计费接口名。
 */
export function getVideoHdToolName(params: VideoHdParams) {
  if (params.hd_type === VideoSRMode._2K || params.hd_type === VideoSRMode._4K) {
    return AI_TOOL_ALGORITHM_URI.VIDEO_HD_ALL_IN_ONE;
  }
  if (params.hd_type === VideoSRMode.LowLight) return AI_TOOL_ALGORITHM_URI.VIDEO_HD_LOW_LIGHT;
  if (params.hd_type === VideoSRMode.DeAI) return AI_TOOL_ALGORITHM_URI.VIDEO_HD_DE_AI;
  if (params.hd_type === VideoSRMode.Game) return AI_TOOL_ALGORITHM_URI.VIDEO_HD_GAME;
  if (params.hd_type === VideoSRMode.Anime) return AI_TOOL_ALGORITHM_URI.VIDEO_HD_ANIME;
  if (params.hd_type === VideoSRMode.Portrait) return AI_TOOL_ALGORITHM_URI.VIDEO_HD_PORTRAIT;
  if (params.hd_type === VideoSRMode.Goods) return AI_TOOL_ALGORITHM_URI.VIDEO_HD_GOODS;
  return AI_TOOL_ALGORITHM_URI.VIDEO_HD_AI_4K;
}

export function getBackgroundReplaceToolName(params: BackgroundReplaceParams) {
  return params.background_image
    ? AI_TOOL_ALGORITHM_URI.BACKGROUND_REPLACE_REF
    : AI_TOOL_ALGORITHM_URI.BACKGROUND_REPLACE_GENERAL;
}
